import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { hasCapability } from "@/lib/brokerAuth";
import { getUnseenCount } from "@/lib/brokerNotifications";
import { NotificationsPanel } from "@/components/NotificationsPanel";
import { TOUCH_SQUARE } from "@/components/buttonStyles";

// How often the bell re-polls the unseen count. No WS/SSE — a short interval poll
// is the portal's idiom (useRunDetails / TearSheetAutoRefresh). Exported for tests.
export const NOTIFICATIONS_POLL_MS = 60_000;

// The top-bar notification bell (BRK-S157 / PMS-S353), with a real UNSEEN-count
// badge. Gated on notification:view: hidden entirely for a broker without it.
// It polls the count on an interval and re-reads it on a route change. A failed
// read keeps the last value and never white-screens (#7).
//
// Since BRK-S252 it opens the notifications panel (NotificationsPanel) instead
// of linking to the inbox. PMS marks the rows the panel reads as seen, so the
// bell re-reads its count once they load, and the badge clears. A route change
// (a row's link, See all, Back) closes the panel.
export function NotificationBell() {
  const { identity, authedRequest } = useAuth();
  const canView = hasCapability(identity, "notification:view");
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const pathname = useLocation().pathname;
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(() => {
    getUnseenCount(authedRequest)
      .then((c) => {
        if (mounted.current) setCount(c);
      })
      .catch(() => {
        /* fail soft — keep the last count */
      });
  }, [authedRequest]);

  useEffect(() => {
    if (!canView) return;
    refresh();
    const timer = setInterval(refresh, NOTIFICATIONS_POLL_MS);
    return () => clearInterval(timer);
  }, [canView, refresh, pathname]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  if (!canView) return null;

  const badge = count > 99 ? "99+" : String(count);
  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={
          count > 0 ? `Notifications, ${count} unseen` : "Notifications"
        }
        onClick={() => setOpen((prev) => !prev)}
        className={`relative inline-flex h-8 w-8 items-center justify-center rounded-full border border-brk-border text-brk-dim transition-colors hover:bg-brk-bg hover:text-brk-text ${TOUCH_SQUARE}`}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6M10.5 20a1.5 1.5 0 0 0 3 0"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-brk-accent px-1 text-badge text-white">
            {badge}
          </span>
        )}
      </button>
      <NotificationsPanel
        open={open}
        onClose={() => setOpen(false)}
        onLoaded={refresh}
      />
    </>
  );
}
